import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { getCurrentUser } from './firebase'

// Soru bankası tipi
export interface QuestionBank {
  id: string
  name: string
  subject: string
  publisher: string
  totalQuestions: number
  completedQuestions: number
  price: number
  isFavorite: boolean
  color: string
  userId: string
  createdAt: string
  updatedAt: string
}

// Koleksiyon adı
const QUESTION_BANKS_COLLECTION = 'questionBanks'

// Soru bankası servisleri
export const questionBankService = {
  // Tüm soru bankalarını getir
  getQuestionBanks: async (): Promise<QuestionBank[]> => {
    try {
      const user = getCurrentUser()
      if (!user) {
        throw new Error('Kullanıcı giriş yapmamış')
      }

      const q = query(
        collection(db, QUESTION_BANKS_COLLECTION),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      )
      
      const querySnapshot = await getDocs(q)
      const questionBanks: QuestionBank[] = []
      
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        questionBanks.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString()
        } as QuestionBank)
      })
      
      return questionBanks
    } catch (error) {
      console.error('Soru bankaları getirilemedi:', error)
      throw error
    }
  },

  // Yeni soru bankası ekle
  addQuestionBank: async (questionBankData: Omit<QuestionBank, 'id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const user = getCurrentUser()
      if (!user) {
        throw new Error('Kullanıcı giriş yapmamış')
      }

      const docRef = await addDoc(collection(db, QUESTION_BANKS_COLLECTION), {
        ...questionBankData,
        userId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      
      return docRef.id
    } catch (error) {
      console.error('Soru bankası eklenemedi:', error)
      throw error
    }
  },

  // Soru bankasını güncelle
  updateQuestionBank: async (id: string, updates: Partial<Omit<QuestionBank, 'id' | 'userId' | 'createdAt'>>): Promise<void> => {
    try {
      const user = getCurrentUser()
      if (!user) {
        throw new Error('Kullanıcı giriş yapmamış')
      }

      const docRef = doc(db, QUESTION_BANKS_COLLECTION, id)
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Soru bankası güncellenemedi:', error)
      throw error
    }
  },

  // Soru bankasını sil
  deleteQuestionBank: async (id: string): Promise<void> => {
    try {
      const user = getCurrentUser()
      if (!user) {
        throw new Error('Kullanıcı giriş yapmamış')
      }

      const docRef = doc(db, QUESTION_BANKS_COLLECTION, id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error('Soru bankası silinemedi:', error)
      throw error
    }
  },

  // İlerleme güncelle
  updateProgress: async (id: string, completedQuestions: number): Promise<void> => {
    try {
      await questionBankService.updateQuestionBank(id, { completedQuestions })
    } catch (error) {
      console.error('İlerleme güncellenemedi:', error)
      throw error
    }
  },

  // Favorilere ekle/çıkar
  toggleFavorite: async (id: string, isFavorite: boolean): Promise<void> => {
    try {
      await questionBankService.updateQuestionBank(id, { isFavorite })
    } catch (error) {
      console.error('Favori durumu güncellenemedi:', error)
      throw error
    }
  }
}

export default questionBankService